// src/features/dashboard/hooks/Employee/useEmployeeActiveHours.ts

import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import { formatApiDate } from '../../utils/date.utils';

import {
  getActiveHoursKpiCards,
  getHourlyActiveMinutes,
  getTimeUtilisation,
  getWtdActiveHours,
  getMeVsTeam,
  getFocusBlocks,
  getHistory30Days,
  getWeeklyActiveHoursDetail,
} from '../../services/Employee/employeeActiveHours.service';

import {
  ActiveHoursKpiResponse,
  HourlyActiveMinutesResponse,
  HourlyActiveMinutesItem,
  TimeUtilisationResponse,
  WtdActiveHoursResponse,
  MeVsTeamResponse,
  FocusBlocksResponse,
  ActiveHoursHistory30DaysResponse,
  WeeklyActiveHoursDetailResponse,
} from '../../types/EmployeeActiveHours/employeeActiveHours.types';

import { useDashboardDate } from '../../context/DashboardDateContext';

import { AuthSession } from '../../../../utils/authSession';

/* ==========================================================================
   EMPTY VALUES
   ========================================================================== */

const EMPTY_KPI: ActiveHoursKpiResponse = {
  activeToday: '00:00:00',
  utilization: 0,
  sevenDayAvg: '00:00:00',
  peakHour: '',
  vsTeamAvg: '00:00:00',
};

const EMPTY_UTILISATION: TimeUtilisationResponse = {
  activePercent: 0,
  activeCount: 0,
  idlePercent: 0,
  idleCount: 0,
  meetingPercent: 0,
  meetingCount: 0,
  totalLoggedHours: '',
  utilization: 0,
};

const EMPTY_ME_VS_TEAM: MeVsTeamResponse = {
  manager: {
    managerCode: '',
    managerName: '',
    employeeName: '',
  },
  trend: [],
};

const EMPTY_FOCUS: FocusBlocksResponse = {
  blocks: [],
  summary: {
    totalFocus: '',
    avgSwitches: 0,
  },
};

const EMPTY_HISTORY: ActiveHoursHistory30DaysResponse = {
  history: [],
  months: [],
};

/* ==========================================================================
   HELPERS
   ========================================================================== */

const getDateNumber = (
  date?: string | null,
): string => {
  if (!date) {
    return '';
  }

  const datePart =
    date.split('T')[0];

  const parts =
    datePart.split('-');

  return parts.length === 3
    ? parts[2]
    : '';
};

const getDayWithDate = (
  dayName?: string | null,
  workDate?: string | null,
): string => {
  const shortDay =
    dayName
      ?.substring(0, 3)
      .trim() || '';

  const dateNumber =
    getDateNumber(workDate);

  if (!shortDay) {
    return dateNumber;
  }

  if (!dateNumber) {
    return shortDay;
  }

  return `${shortDay} ${dateNumber}`;
};

const normalizeTimeValue = (
  value: unknown,
): string => {
  if (
    value === null ||
    value === undefined
  ) {
    return '00:00:00';
  }

  const stringValue =
    String(value).trim();

  if (!stringValue) {
    return '00:00:00';
  }

  return stringValue;
};

const normalizeNumber = (
  value: unknown,
): number => {
  const numberValue =
    Number(value);

  return Number.isFinite(
    numberValue,
  )
    ? numberValue
    : 0;
};

/* ==========================================================================
   HOOK
   ========================================================================== */

export const useEmployeeActiveHours = () => {
  const {
    fromDate,
    toDate,
  } = useDashboardDate();

  const employeeCode =
    AuthSession.employeeCode();

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');

  const [kpis, setKpis] =
    useState<ActiveHoursKpiResponse>(
      EMPTY_KPI,
    );

  const [hourly, setHourly] =
    useState<
      HourlyActiveMinutesItem[]
    >([]);

  const [
    hourlyTarget,
    setHourlyTarget,
  ] = useState<number>(45);

  const [
    utilisation,
    setUtilisation,
  ] = useState<TimeUtilisationResponse>(
    EMPTY_UTILISATION,
  );

  const [wtdTrend, setWtdTrend] =
    useState<
      WtdActiveHoursResponse[]
    >([]);

  const [meVsTeam, setMeVsTeam] =
    useState<MeVsTeamResponse>(
      EMPTY_ME_VS_TEAM,
    );

  const [
    focusBlocks,
    setFocusBlocks,
  ] = useState<FocusBlocksResponse>(
    EMPTY_FOCUS,
  );

  const [
    history30Days,
    setHistory30Days,
  ] =
    useState<ActiveHoursHistory30DaysResponse>(
      EMPTY_HISTORY,
    );

  const [
    weeklyDetail,
    setWeeklyDetail,
  ] = useState<
    WeeklyActiveHoursDetailResponse[]
  >([]);

  /* ==========================================================================
     LOAD DATA
     ========================================================================== */

  const loadData =
    useCallback(
      async () => {
        try {
          setLoading(true);
          setError('');

          /* ================================================================
             DATES
             ================================================================ */

          const from =
            formatApiDate(fromDate);

          const to =
            formatApiDate(toDate);

          /* ================================================================
             API CALLS
             ================================================================ */

          const [
            kpi,
            hourlyData,
            utilisationData,
            wtd,
            meTeam,
            focus,
            history,
            weekly,
          ] = await Promise.all([
            getActiveHoursKpiCards({
              employeeCode,
              fromDate: from,
              toDate: to,
            }),

            getHourlyActiveMinutes({
              employeeCode,
              date: from,
            }),

            getTimeUtilisation({
              employeeCode,
              date: from,
            }),

            getWtdActiveHours({
              employeeCode,
              currentDate: from,
            }),

            getMeVsTeam({
              employeeCode,
              currentDate: from,
            }),

            getFocusBlocks({
              employeeCode,
              date: from,
              minFocusMinutes: 120,
            }),

            getHistory30Days({
              employeeCode,
              currentDate: from,
            }),

            getWeeklyActiveHoursDetail({
              employeeCode,
              currentDate: from,
              minFocusMinutes: 20,
            }),
          ]);

          /* ================================================================
             KPI
             ================================================================ */

          const normalizedKpi:
            ActiveHoursKpiResponse = {
            activeToday:
              normalizeTimeValue(
                kpi?.activeToday,
              ),

            utilization:
              normalizeNumber(
                kpi?.utilization,
              ),

            sevenDayAvg:
              normalizeTimeValue(
                kpi?.sevenDayAvg,
              ),

            peakHour:
              kpi?.peakHour
                ? String(
                    kpi.peakHour,
                  )
                : '',

            vsTeamAvg:
              normalizeTimeValue(
                kpi?.vsTeamAvg,
              ),
          };

          setKpis(
            normalizedKpi,
          );

          /* ================================================================
             HOURLY ACTIVE MINUTES
             ================================================================ */

          /*
           * API RESPONSE:
           *
           * {
           *   hourlyActiveMinutesTarget: "45",
           *   employeeHourlyActiveMinutesList: [...]
           * }
           */

          const rawHourly =
            hourlyData as
              | HourlyActiveMinutesResponse
              | null
              | undefined;

          const target =
            normalizeNumber(
              rawHourly
                ?.hourlyActiveMinutesTarget,
            );

          const hourlyList =
            Array.isArray(
              rawHourly
                ?.employeeHourlyActiveMinutesList,
            )
              ? rawHourly
                  .employeeHourlyActiveMinutesList
                  .map(
                    (
                      item,
                    ) => ({
                      hour:
                        normalizeNumber(
                          item.hour,
                        ),

                      activeMinutes:
                        normalizeNumber(
                          item.activeMinutes,
                        ),
                    }),
                  )
              : [];

          /*
           * Keep API target dynamic.
           *
           * If API sends 45:
           * target = 45
           *
           * If API sends another value:
           * target automatically changes.
           */

          setHourlyTarget(
            target > 0
              ? target
              : 45,
          );

          setHourly(
            hourlyList,
          );

          console.log(
            'Hourly Active Minutes API:',
            hourlyData,
          );

          console.log(
            'Hourly Active Minutes Target:',
            target,
          );

          console.log(
            'Hourly Active Minutes List:',
            hourlyList,
          );

          /* ================================================================
             UTILISATION
             ================================================================ */

          const normalizedUtilisation:
            TimeUtilisationResponse = {
            activePercent:
              normalizeNumber(
                utilisationData?.activePercent,
              ),

            activeCount:
              normalizeNumber(
                utilisationData?.activeCount,
              ),

            idlePercent:
              normalizeNumber(
                utilisationData?.idlePercent,
              ),

            idleCount:
              normalizeNumber(
                utilisationData?.idleCount,
              ),

            meetingPercent:
              normalizeNumber(
                utilisationData?.meetingPercent,
              ),

            meetingCount:
              normalizeNumber(
                utilisationData?.meetingCount,
              ),

            totalLoggedHours:
              utilisationData?.totalLoggedHours
                ? String(
                    utilisationData.totalLoggedHours,
                  )
                : '',

            utilization:
              normalizeNumber(
                utilisationData?.utilization,
              ),
          };

          setUtilisation(
            normalizedUtilisation,
          );

          /* ================================================================
             WTD ACTIVE HOURS
             ================================================================ */

          const formattedWtd =
            Array.isArray(wtd)
              ? wtd.map(
                  (item: any) => {
                    const formattedDay =
                      getDayWithDate(
                        item.dayName,
                        item.workDate,
                      );

                    return {
                      ...item,

                      day:
                        formattedDay ||
                        item.day ||
                        item.dayName,
                    };
                  },
                )
              : [];

          setWtdTrend(
            formattedWtd as WtdActiveHoursResponse[],
          );

          /* ================================================================
             ME VS TEAM
             ================================================================ */

          const formattedMeVsTeam =
            meTeam?.trend
              ? meTeam.trend.map(
                  (item: any) => {
                    const formattedDay =
                      getDayWithDate(
                        item.dayName,
                        item.workDate,
                      );

                    return {
                      ...item,

                      day:
                        formattedDay ||
                        item.day ||
                        item.dayName,
                    };
                  },
                )
              : [];

          setMeVsTeam({
            ...meTeam,

            trend:
              formattedMeVsTeam,
          });

          /* ================================================================
             FOCUS BLOCKS
             ================================================================ */

          setFocusBlocks(
            focus,
          );

          /* ================================================================
             HISTORY
             ================================================================ */

          setHistory30Days(
            history,
          );

          /* ================================================================
             WEEKLY DETAIL
             ================================================================ */

          setWeeklyDetail(
            Array.isArray(weekly)
              ? weekly
              : [],
          );
        } catch (err) {
          console.error(
            'Employee Active Hours:',
            err,
          );

          setError(
            'Failed to load Active Hours report.',
          );
        } finally {
          setLoading(false);
        }
      },
      [
        employeeCode,
        fromDate,
        toDate,
      ],
    );

  /* ==========================================================================
     EFFECT
     ========================================================================== */

  useEffect(() => {
    if (!employeeCode) {
      setLoading(false);

      setError(
        'Employee information is not available.',
      );

      return;
    }

    loadData();
  }, [
    loadData,
    employeeCode,
  ]);

  /* ==========================================================================
     RETURN
     ========================================================================== */

  return {
    loading,
    error,

    kpis,

    hourly,

    /*
     * Dynamic target returned separately
     * so the chart can display the API target.
     */
    hourlyTarget,

    utilisation,

    wtdTrend,

    meVsTeam,

    focusBlocks,

    history30Days,

    weeklyDetail,

    reload: loadData,
  };
};